import { useCallback, useEffect, useRef, useState } from 'react'
import type { ISODate } from '@app/shared'

import { isDemoMode, useUploadVoiceMemo } from '../../lib/data'
import { X } from '../icons'

// ============================================================================
// THE RECORD BUTTON, LIVE — docs/design/handoff/"LifeOS Home Options.dc.html"
// option 3c: a 54px green circle, a six-bar level meter beside it, and the line
// "Tap to talk it out instead" underneath.
//
// WHAT HAPPENS WHEN IT IS TAPPED (the deployed V1 contract, apps/worker/src/routes/audio.ts):
//   1. getUserMedia → MediaRecorder, in the browser. Nothing leaves the tab yet.
//   2. Tap again → the blob.
//   3. POST /api/audio/uploads  { kind: 'memo', origin: 'web', journalDate }  → { jobId, uploadUrl }
//   4. PUT the bytes at uploadUrl. The job becomes `queued` and the desktop worker can see it.
// The row then says "waiting for desktop worker" until the PC returns a transcript, which is the
// literal truth: the transcription happens on a machine that may be asleep.
//
// ── THE THREE THINGS THIS COMPONENT REFUSES TO DO ───────────────────────────
//
// 1. IT DOES NOT RE-ENCODE. Whatever MediaRecorder produced is what goes up — `audio/webm` with
//    Opus in every browser that matters here. faster-whisper decodes through PyAV (av==18.1.0 in
//    apps/pc-worker/requirements.txt), which is ffmpeg, which reads WebM/Opus; and the worker's
//    own AUDIO_EXTENSIONS map already lists `.webm`. Converting in the browser would put a lossy
//    step between the microphone and the transcript for no reason anyone could point at.
//
// 2. IT DOES NOT DROP A RECORDING QUIETLY. If the upload fails the blob stays in memory and the
//    control offers one retry, because a memo lost to a bus tunnel is the failure this whole
//    pipeline exists to avoid. Leaving the page is what discards it, and nothing pretends
//    otherwise.
//
// 3. IT DOES NOT RECORD IN DEMO MODE. A memo's life is R2 plus a GPU on a desktop; demo mode has
//    neither. The button stays inert and says so, rather than accepting audio it would have to
//    throw away (see demo.ts → uploadVoiceMemo).
// ============================================================================

/**
 * A memo is a thought, not a lecture — ten minutes is already far past the point where writing
 * would have been faster. The cap is enforced locally so the recording stops at a length that is
 * certain to fit under the Worker's 100 MB body limit, instead of failing after the upload.
 */
const MAX_SECONDS = 600
/** When the countdown starts being said out loud, so the cap is never a surprise. */
const WARN_AT_SECONDS = 540

/**
 * In preference order. Opus in WebM is what Chrome, Edge and Firefox all produce; `audio/mp4` is
 * the Safari fallback. The first supported one wins, and if none is the recorder says so instead
 * of producing a file the desktop worker cannot open.
 */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']

/** The mockup's six bars, in order. They are the resting shape and the ceiling of the live one. */
const BAR_HEIGHTS = [9, 20, 14, 24, 11, 18]

type Phase = 'idle' | 'starting' | 'recording' | 'uploading' | 'failed'

function mmss(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = Math.floor(totalSeconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

/** The first mime type this browser will actually record. Null when it will not record at all. */
function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  const supported = MediaRecorder.isTypeSupported
  if (typeof supported !== 'function') return null
  return MIME_CANDIDATES.find((type) => supported.call(MediaRecorder, type)) ?? null
}

export function VoiceRecorder({ date }: { date: ISODate }) {
  const upload = useUploadVoiceMemo()

  const [phase, setPhase] = useState<Phase>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [level, setLevel] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)

  // Everything the browser handed us that has to be given back. Refs, not state: none of it
  // affects a render, and all of it has to be reachable from a cleanup that depends on nothing.
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const audioCtx = useRef<AudioContext | null>(null)
  const frame = useRef<number | null>(null)
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAt = useRef(0)
  const chunks = useRef<Blob[]>([])
  /** The finished recording, kept only so a failed upload can be retried. */
  const pending = useRef<{ blob: Blob; durationSec: number } | null>(null)

  const releaseHardware = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    frame.current = null
    if (ticker.current) clearInterval(ticker.current)
    ticker.current = null
    // The microphone indicator in the browser chrome stays lit until every track is stopped, and a
    // recorder that leaves it on looks exactly like one that is still listening.
    stream.current?.getTracks().forEach((track) => track.stop())
    stream.current = null
    void audioCtx.current?.close().catch(() => {})
    audioCtx.current = null
    recorder.current = null
    setLevel(0)
  }, [])

  useEffect(() => releaseHardware, [releaseHardware])

  const send = useCallback(
    (blob: Blob, durationSec: number) => {
      pending.current = { blob, durationSec }
      setPhase('uploading')
      setProblem(null)
      upload.mutate(
        { date, blob, durationSec },
        {
          onSuccess: () => {
            pending.current = null
            setPhase('idle')
            setElapsed(0)
          },
          onError: (error) => {
            // The blob is deliberately NOT cleared: `pending` is what the retry sends.
            setPhase('failed')
            setProblem(error instanceof Error ? error.message : 'The upload did not go through.')
          },
        },
      )
    },
    [date, upload],
  )

  const stop = useCallback(() => {
    const active = recorder.current
    if (!active || active.state === 'inactive') return
    const durationSec = Math.max(1, Math.round((performance.now() - startedAt.current) / 1000))
    active.onstop = () => {
      const blob = new Blob(chunks.current, { type: active.mimeType || 'audio/webm' })
      chunks.current = []
      releaseHardware()
      if (blob.size === 0) {
        setPhase('failed')
        setProblem(
          'The recording came back empty, so nothing was uploaded. Check that the right microphone is selected and try again.',
        )
        return
      }
      send(blob, durationSec)
    }
    active.stop()
  }, [releaseHardware, send])

  // THE DISCREET WAY OUT. Unlike `stop`, this never reaches `send`: the recorder is told to throw
  // its own callbacks away before it stops, so the tail chunk it flushes on stop() lands nowhere,
  // nothing is queued for upload, and no row is ever written. Only reachable mid-recording — the
  // button that calls this does not exist in any other phase.
  const cancel = useCallback(() => {
    const active = recorder.current
    if (active && active.state !== 'inactive') {
      active.ondataavailable = null
      active.onstop = null
      active.stop()
    }
    chunks.current = []
    pending.current = null
    releaseHardware()
    setPhase('idle')
    setElapsed(0)
    setProblem(null)
  }, [releaseHardware])

  // `stop` is reached through a ref inside the run loop below, so the loop can be started once per
  // recording rather than rebuilt whenever a dependency of `stop` changes mid-recording.
  const stopRef = useRef(stop)
  stopRef.current = stop

  async function start() {
    setProblem(null)

    const mimeType = pickMimeType()
    if (!navigator.mediaDevices?.getUserMedia || !mimeType) {
      setPhase('failed')
      setProblem(
        'This browser cannot record audio. Chrome, Edge and Firefox can — or drop the file into ' +
          'the desktop worker’s folder, which does not go through the browser at all.',
      )
      return
    }

    setPhase('starting')
    let media: MediaStream
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (error) {
      setPhase('failed')
      const name = error instanceof Error ? error.name : ''
      setProblem(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'The browser blocked the microphone, so nothing was recorded. Allow it for this site ' +
              '(the icon at the end of the address bar), then tap again.'
          : name === 'NotFoundError' || name === 'NotReadableError'
            ? 'No microphone was available, so nothing was recorded. Plug one in or close whatever ' +
              'else is using it, then tap again.'
            : 'The microphone could not be opened, so nothing was recorded.',
      )
      return
    }

    stream.current = media
    chunks.current = []
    const active = new MediaRecorder(media, { mimeType })
    active.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.current.push(event.data)
    }
    recorder.current = active
    startedAt.current = performance.now()
    setElapsed(0)
    setPhase('recording')
    active.start()

    // THE ELAPSED CLOCK reads the wall clock rather than counting ticks: a throttled background
    // tab would otherwise show a timer that disagrees with the audio it is timing. It is also
    // what enforces the cap, so a recording left running stops itself.
    ticker.current = setInterval(() => {
      const seconds = (performance.now() - startedAt.current) / 1000
      setElapsed(seconds)
      if (seconds >= MAX_SECONDS) stopRef.current()
    }, 250)

    // THE LEVEL METER IS THE REAL SIGNAL, not a decoration on a timer. Bars that move whether or
    // not the microphone is picking anything up would let a whole memo be recorded into a muted
    // input with the screen saying everything is fine.
    try {
      const ctx = new AudioContext()
      audioCtx.current = ctx
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 512
      ctx.createMediaStreamSource(media).connect(analyser)
      const samples = new Uint8Array(analyser.fftSize)
      const read = () => {
        analyser.getByteTimeDomainData(samples)
        let sum = 0
        for (const sample of samples) {
          const centred = (sample - 128) / 128
          sum += centred * centred
        }
        // RMS, lifted onto a curve that makes ordinary speech fill most of the meter instead of
        // sitting at the bottom of it.
        setLevel(Math.min(1, Math.sqrt(sum / samples.length) * 3.2))
        frame.current = requestAnimationFrame(read)
      }
      frame.current = requestAnimationFrame(read)
    } catch {
      // No AudioContext is not a reason to lose a recording. The bars stay at rest and the timer,
      // which is the part that matters, keeps running.
    }
  }

  const recording = phase === 'recording'
  const remaining = Math.max(0, MAX_SECONDS - elapsed)
  const inert = isDemoMode || phase === 'starting' || phase === 'uploading'

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={inert}
          onClick={() => (recording ? stop() : void start())}
          title={
            isDemoMode
              ? 'Demo mode has no microphone path: a memo is uploaded to R2 and transcribed by the desktop worker, and neither exists without an account. The memos below are sample data.'
              : recording
                ? 'Stop recording and send it to the desktop worker'
                : 'Record a voice memo. It uploads immediately and is transcribed on your PC.'
          }
          aria-label={recording ? 'Stop recording' : 'Record a voice memo'}
          className={[
            'flex size-[54px] flex-none items-center justify-center rounded-full',
            'transition-[background-color,filter] duration-[var(--dur-hover)] ease-[var(--ease-ink)]',
            isDemoMode || phase === 'uploading' || phase === 'starting'
              ? 'bg-dash'
              : recording
                ? 'bg-danger-dot hover:brightness-90'
                : 'bg-success-dot shadow-record hover:brightness-90',
          ].join(' ')}
        >
          {recording ? (
            <span className="size-[17px] rounded-[4px] bg-surface" aria-hidden />
          ) : (
            <span className="h-[19px] w-[11px] rounded-keycap bg-surface" aria-hidden />
          )}
        </button>

        {/* A fixed-width slot beside the stop control, always taking up the same space, so the
            level meter never shifts when the cancel affordance appears or disappears — it only
            exists in the DOM while a recording is actually in progress. Quiet on purpose: this
            discards, and a loud button next to Stop invites a mis-tap. */}
        <span className="flex w-[22px] flex-none items-center justify-center">
          {recording && (
            <button
              type="button"
              onClick={cancel}
              title="Discard this recording — nothing is uploaded"
              aria-label="Cancel recording"
              className="flex size-[22px] items-center justify-center rounded-full text-faint transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-muted"
            >
              <X size={13} strokeWidth={2} />
            </button>
          )}
        </span>

        <LevelBars level={recording ? level : 0} active={recording} />
      </div>

      {/* One line, and it always says what is actually happening. `aria-live` because the state
          changes without the focus moving — the timer and the queue hand-off both announce. */}
      <p aria-live="polite" className="text-[12px] leading-[1.5] text-muted text-pretty">
        {isDemoMode && 'Demo mode has no microphone path — the memos below are sample data.'}
        {!isDemoMode && phase === 'idle' && 'Tap to talk it out instead'}
        {phase === 'starting' && 'Waiting for the microphone…'}
        {recording && (
          <>
            <span className="font-semibold tabular-nums text-danger-2">{mmss(elapsed)}</span>
            {elapsed >= WARN_AT_SECONDS
              ? ` · stops itself in ${mmss(remaining)} — a memo caps at ${mmss(MAX_SECONDS)}`
              : ' · tap to stop'}
          </>
        )}
        {phase === 'uploading' && 'Uploading — it enters the transcription queue when this finishes.'}
      </p>

      {problem && (
        <div className="rounded-row border border-danger-line bg-danger-fill px-3 py-[9px]">
          <p className="text-[11px] leading-[1.45] text-danger text-pretty">{problem}</p>
          {/* Exactly one action, and only when there is genuinely something to retry. */}
          {pending.current && (
            <button
              type="button"
              onClick={() => {
                const held = pending.current
                if (held) send(held.blob, held.durationSec)
              }}
              className="mt-2 rounded-button bg-danger-solid px-3 py-[5px] text-[11px] font-semibold leading-none text-on-dark"
            >
              Try the upload again
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The six bars from the mockup. At rest they hold their drawn heights in `dash-2`; while
 * recording each one is scaled by the live input level, with a fixed per-bar offset so the group
 * reads as a waveform rather than six identical bars going up and down together.
 */
function LevelBars({ level, active }: { level: number; active: boolean }) {
  return (
    <div className="flex h-[30px] items-center gap-[2px]" aria-hidden>
      {BAR_HEIGHTS.map((height, i) => {
        const offset = [1, 0.72, 0.9, 0.62, 1, 0.8][i]!
        const scaled = active ? Math.max(3, height * (0.22 + level * offset)) : height
        return (
          <span
            key={i}
            className={[
              'w-[2px] rounded-[2px] transition-[height] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              active ? 'bg-danger-dot' : 'bg-dash-2',
            ].join(' ')}
            style={{ height: `${Math.round(scaled)}px` }}
          />
        )
      })}
    </div>
  )
}
